import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom"
import { Nav, NavItem } from "reactstrap";
import { AppDispatch, RootState } from "store";
import { XAxis, YAxis, Tooltip, ResponsiveContainer, Area, CartesianGrid, AreaChart } from "recharts";
import Flatpickr from "react-flatpickr";
import moment from "moment";
import { fetchCampaignPerformance } from "slices/campaigns/performance/thunk";
import { debounce } from "lodash";
import { formatNumber } from "utils/helpers";

const CampaignPerformance = () => {

    const dispatch: AppDispatch = useDispatch();
    const { campaignId } = useParams();
    const defaultDateRange = useSelector((state: RootState) => state.campaignPerformance.default_date_range);
    const campaignPerformanceData = useSelector((state: RootState) => state.campaignPerformance.campaigns[campaignId!]?.[defaultDateRange!]?.performance);

    const [activeTab, setActiveTab] = useState<"cpv" | "cpe" | "total_interactions" | "views">('views');

    const [chartData, setChartData] = useState<{ date: string, value: string | number }[]>([]);

    useEffect(() => {
        if(['cpe', 'cpv'].includes(activeTab) && campaignPerformanceData?.overall_trend?.monetary){
            const data = campaignPerformanceData.overall_trend.monetary.map((trend) => {
                return { date: trend.period_timestamp, value: trend.values[activeTab as keyof typeof trend.values], type: 'monetary' }
            })
            setChartData(data);
        } else if (['total_interactions', 'views'].includes(activeTab) && campaignPerformanceData?.overall_trend?.total) {
            const data = campaignPerformanceData.overall_trend.total.map((trend) => {
                return { date: trend.period_timestamp, value: trend.values[activeTab as keyof typeof trend.values], type: 'total' }
            })
            setChartData(data);
        }
    }, [campaignPerformanceData, activeTab])


    const CustomTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className="chart-tooltip">
                    <span>{payload[0]?.payload?.type === 'monetary' ? `₹${payload[0].value.toFixed(2)}` : formatNumber(payload[0].value)}</span>
                </div>
            );
        }
        return null;
    };

    const getLatestValue = (key: 'cpv' | 'cpe' | 'total_interactions' | 'views') => {
        if(['cpe', 'cpv'].includes(key) && Array.isArray(campaignPerformanceData?.overall_trend?.monetary) && campaignPerformanceData.overall_trend.monetary.length > 0){
            const last = campaignPerformanceData.overall_trend.monetary[campaignPerformanceData.overall_trend.monetary.length - 1];
            return last ? last.values[key as 'cpv' | 'cpe'] : 0;
        }else if (['total_interactions', 'views'].includes(key) && Array.isArray(campaignPerformanceData?.overall_trend?.total) && campaignPerformanceData.overall_trend.total.length > 0) {
            const last = campaignPerformanceData.overall_trend.total[campaignPerformanceData.overall_trend.total.length - 1];
            return last ? last.values[key as 'total_interactions'] : 0;
        }
        return 0;
    }

    const getSelectedDate = () => {
        if (campaignPerformanceData?.campaign) {
            return [moment(campaignPerformanceData.campaign.generate_insights_from, 'YYYY-MM-DD').format("D MMM, YYYY"), moment(campaignPerformanceData.campaign.generate_insights_till, 'YYYY-MM-DD').format("D MMM, YYYY")]
        }
        return []
    }

    const handleDateChange = useCallback(
        debounce((selectedDates: Date[]) => {
            if (selectedDates.length < 2) return;
            const fromDate = moment(selectedDates[0]).format("YYYY-MM-DD");
            const toDate = moment(selectedDates[1]).format("YYYY-MM-DD");
            dispatch(fetchCampaignPerformance({ campaignId: campaignId!, payload: { start_date: fromDate, end_date: toDate } }));
        }, 300),
        [dispatch, campaignId]
    );

    return (
        <div>
            <Nav tabs className="d-flex w-100 performance-tabs justify-content-between">
                <NavItem onClick={() => setActiveTab('views')} className={`performance-tab ${activeTab === 'views' && 'active'} w-25`}>
                    <div className="d-flex flex-column align-items-center justify-content-center h-100">
                        <h3>{formatNumber(getLatestValue('views'))}</h3>
                        <span>Views</span>
                    </div>
                </NavItem>
                <NavItem onClick={() => setActiveTab('total_interactions')} className={`performance-tab ${activeTab === 'total_interactions' && 'active'} w-25`}>
                    <div className="d-flex flex-column align-items-center justify-content-center h-100">
                        <h3>{formatNumber(getLatestValue('total_interactions'))}</h3>
                        <span>Total Interactions</span>
                    </div>
                </NavItem>
                <NavItem onClick={() => setActiveTab('cpv')} className={`performance-tab ${activeTab === 'cpv' && 'active'} w-25`}>
                    <div className="d-flex flex-column align-items-center justify-content-center h-100">
                        <h3>₹{getLatestValue('cpv')}</h3>
                        <span>Cost Per View (CPV)</span>
                    </div>
                </NavItem>
                <NavItem onClick={() => setActiveTab('cpe')} className={`performance-tab ${activeTab === 'cpe' && 'active'} w-25`}>
                    <div className="d-flex flex-column align-items-center justify-content-center h-100">
                        <h3>₹{getLatestValue('cpe')}</h3>
                        <span>Cost Per Engagement (CPE)</span>
                    </div>
                </NavItem>
            </Nav>
            <div className="d-flex justify-content-end align-items-center my-3 px-5">
                {
                    campaignPerformanceData &&
                    <div className="input-group w-auto performance-date-range">
                        <Flatpickr
                            className="border-0 dash-filter-picker performance-date-range-picker"
                            options={{
                                mode: "range",
                                dateFormat: "d M, Y",
                                defaultDate: getSelectedDate(),
                                minDate: moment(campaignPerformanceData.campaign.generate_insights_from).toDate(),
                                maxDate: moment(campaignPerformanceData.campaign.generate_insights_till).toDate()
                            }}
                            onChange={(selectedDates) => handleDateChange(selectedDates)}
                        />
                        <div className="bg-transparent border-0"><i className="ri-calendar-2-line"></i></div>
                    </div>
                }
            </div>
            <div className="mt-4 pe-5">
                <ResponsiveContainer width="100%" height={205}>
                    <AreaChart data={chartData}>
                        <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} tickFormatter={formatNumber} />
                        <Tooltip content={<CustomTooltip />} />
                        <CartesianGrid vertical={false} strokeDasharray="2 2" />
                        <Area type="linear" dataKey="value" stroke="#2FAFB7" fill="#E1FDFF" fillOpacity={1} dot={{ r: 2, fill: '#2FAFB7', strokeWidth: 1 }} />
                    </AreaChart>
                </ResponsiveContainer>
            </div>

        </div>
    )
}

export default CampaignPerformance